import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, MenuItem, Select, Typography } from '@mui/material'
import LanguageIcon from '@mui/icons-material/Language'
import { useAppStore } from '@/store/appStore'
import { useLanguagePrompt } from '@/hooks/useLanguagePrompt'
import { useUpdateLanguagePreference } from '@/hooks/useUpdateLanguagePreference'

/**
 * First-run language chooser.
 *
 * Shown once to a user who has never picked a content language while the server offers more than
 * one (see `useLanguagePrompt`). The message and the Continue button render in the language
 * currently highlighted in the selector, not the app's active language, so the choice is legible
 * to someone who cannot read the default. Nothing is committed until Continue: only then is the
 * selection applied, persisted to the profile, and the content bundle for it fetched
 * (`useContentBundle` stays disabled while the prompt is pending).
 *
 * Closable only by Continue: there is no dismiss affordance and no backdrop click, because the
 * app behind it has no content loaded yet.
 */
export function LanguageSelectionOverlay() {
  const { i18n } = useTranslation()
  const { isPrompting, languages } = useLanguagePrompt()
  const selectedLanguage = useAppStore((s) => s.selectedLanguage)
  const setSelectedLanguage = useAppStore((s) => s.setSelectedLanguage)
  const setLanguagePromptResolved = useAppStore((s) => s.setLanguagePromptResolved)
  const { mutate: persistLanguagePreference } = useUpdateLanguagePreference()

  const [previewLang, setPreviewLang] = useState(selectedLanguage)
  // The translator is held in state rather than derived: getFixedT resolves against i18next's
  // store at call time, so it has to be re-taken once the previewed language's file has landed.
  // Until then it renders the English fallback, which is still readable.
  const [tp, setTp] = useState(() => i18n.getFixedT(previewLang))

  useEffect(() => {
    let cancelled = false
    void i18n.loadLanguages(previewLang).finally(() => {
      if (!cancelled) setTp(() => i18n.getFixedT(previewLang))
    })
    return () => {
      cancelled = true
    }
  }, [previewLang, i18n])

  if (!isPrompting) return null

  const handlePreviewChange = (code: string) => {
    setPreviewLang(code)
    setTp(() => i18n.getFixedT(code))
  }

  // A language persisted from an earlier session may no longer be offered (deactivated, or the
  // list changed). Never bind the select to a code that has no option.
  const fallbackLang = languages.find((l) => l.isDefault)?.code ?? languages[0].code
  const activeLang = languages.some((l) => l.code === previewLang) ? previewLang : fallbackLang

  const handleContinue = () => {
    if (activeLang !== selectedLanguage) setSelectedLanguage(activeLang)
    // Persist even when the pre-selected default is kept: the prompt is answered either way,
    // and a stored preference is what stops it reappearing next session.
    persistLanguagePreference(activeLang)
    setLanguagePromptResolved(true)
  }

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="language-prompt-title"
      sx={{
        position: 'fixed',
        inset: 0,
        // Above the content-bundle splash (1400) and every MUI surface.
        zIndex: 9998,
        backgroundColor: 'var(--color-scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          backgroundColor: 'var(--color-white)',
          borderRadius: 2,
          boxShadow: 'var(--shadow-overlay-strong)',
          p: { xs: 3, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
          textAlign: 'center',
        }}
      >
        <LanguageIcon sx={{ fontSize: 40, color: 'var(--color-brand-primary)' }} />

        <Typography id="language-prompt-title" variant="h6" fontWeight={700}>
          {tp('languagePrompt.title')}
        </Typography>

        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
          {tp('languagePrompt.body')}
        </Typography>

        <Select
          value={activeLang}
          onChange={(e) => handlePreviewChange(e.target.value)}
          inputProps={{ 'aria-label': tp('languagePrompt.selectLabel') }}
          // The menu is portaled to document.body, where MUI's default Popover z-index (1300)
          // puts it under this overlay's scrim. Lift it above the overlay itself.
          MenuProps={{ sx: { zIndex: 9999 } }}
          size="small"
          autoFocus
          sx={{ minWidth: 220 }}
        >
          {languages.map((lang) => (
            <MenuItem key={lang.id} value={lang.code}>
              {lang.name}
            </MenuItem>
          ))}
        </Select>

        <Button variant="contained" onClick={handleContinue} sx={{ minWidth: 160 }}>
          {tp('languagePrompt.continue')}
        </Button>
      </Box>
    </Box>
  )
}
